import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import { iExhibitorPassPage } from 'services/Ticketing/Ticketing.type';

interface Row {
  firstname: string;
  lastname: string;
  job_title: string;
  photo: File | null;
}

const emptyRow = (): Row => ({
  firstname: '',
  lastname: '',
  job_title: '',
  photo: null,
});

const box = {
  bgcolor: '#fff',
  border: '1px solid #e5e7eb',
  p: { xs: 2, md: 3 },
  mb: 2.5,
} as const;

/** "Prénom<TAB>Nom<TAB>Fonction" lines pasted from a spreadsheet. */
function parsePasted(text: string): Row[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => {
      const cells = line.split(/\t|;|,/).map((c) => c.trim());
      return {
        firstname: cells[0] ?? '',
        lastname: cells[1] ?? '',
        job_title: cells.slice(2).join(' ').trim(),
        photo: null,
      };
    })
    .filter((r) => !/^pr[ée]nom$/i.test(r.firstname));
}

function Thumb({
  url,
  name,
  size = 48,
}: {
  url: string | null;
  name: string;
  size?: number;
}): JSX.Element {
  return url ? (
    <img
      src={url}
      alt={name}
      style={{
        width: size,
        height: Math.round(size * 1.25),
        objectFit: 'cover',
        border: '1px solid #e5e7eb',
        flexShrink: 0,
      }}
    />
  ) : (
    <Box
      sx={{
        width: size,
        height: Math.round(size * 1.25),
        bgcolor: '#f3f4f6',
        color: '#9ca3af',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {name
        .split(' ')
        .map((p) => p[0] ?? '')
        .join('')
        .slice(0, 2)
        .toUpperCase()}
    </Box>
  );
}

function PhotoPicker({
  file,
  onChange,
  label = 'Photo',
}: {
  file: File | null;
  onChange: (f: File | null) => void;
  label?: string;
}): JSX.Element {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {url && <Thumb url={url} name="" size={32} />}
      <Button
        component="label"
        size="small"
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        {file ? 'Changer' : label}
        <input
          hidden
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            onChange(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
      </Button>
      {file && (
        <Button
          size="small"
          sx={{ textTransform: 'none', color: '#666', minWidth: 0 }}
          onClick={() => onChange(null)}
          aria-label="Retirer la photo"
        >
          ✕
        </Button>
      )}
    </Box>
  );
}

/** Correction of a badge already created. */
function BadgeRow({
  badge,
  code,
  accent,
  onChanged,
  onError,
}: {
  badge: iExhibitorPassPage['badges'][number];
  code: string;
  accent: string;
  onChanged: (p: iExhibitorPassPage) => void;
  onError: (m: string) => void;
}): JSX.Element {
  const { ticketingRepository } = useApiContext();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState({
    firstname: badge.firstname,
    lastname: badge.lastname,
    job_title: badge.job_title ?? '',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const name = `${badge.firstname} ${badge.lastname}`;

  const save = async (removePhoto = false): Promise<void> => {
    setSaving(true);
    try {
      onChanged(
        await ticketingRepository.updateExhibitorBadge(code, badge.id, {
          ...v,
          ...(photo ? { photo } : {}),
          ...(removePhoto ? { remove_photo: true } : {}),
        })
      );
      setEditing(false);
      setPhoto(null);
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        py: 1.5,
        borderTop: '1px solid #f0f0f0',
        flexWrap: 'wrap',
      }}
    >
      <Thumb url={badge.photo_url} name={name} />
      {editing ? (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            size="small"
            label="Prénom"
            value={v.firstname}
            onChange={(e) => setV({ ...v, firstname: e.target.value })}
          />
          <TextField
            size="small"
            label="Nom"
            value={v.lastname}
            onChange={(e) => setV({ ...v, lastname: e.target.value })}
          />
          <TextField
            size="small"
            label="Fonction"
            value={v.job_title}
            onChange={(e) => setV({ ...v, job_title: e.target.value })}
          />
          <PhotoPicker
            file={photo}
            onChange={setPhoto}
            label={badge.photo_url ? 'Changer la photo' : 'Ajouter une photo'}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{name}</Typography>
          <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
            {badge.job_title ?? '—'} ·{' '}
            <span style={{ fontFamily: 'monospace' }}>{badge.code}</span>
            {badge.entered && (
              <span style={{ color: '#166534' }}> · déjà entré(e)</span>
            )}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {editing ? (
          <>
            {badge.photo_url && !photo && (
              <Button
                size="small"
                sx={{ textTransform: 'none', color: '#666' }}
                disabled={saving}
                onClick={async () => await save(true)}
              >
                Retirer la photo
              </Button>
            )}
            <Button
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={() => setEditing(false)}
            >
              Annuler
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={
                saving || v.firstname.trim() === '' || v.lastname.trim() === ''
              }
              onClick={async () => await save()}
              sx={{
                textTransform: 'none',
                bgcolor: accent,
                boxShadow: 'none',
                borderRadius: 0,
                '&:hover': { bgcolor: accent, filter: 'brightness(.9)' },
              }}
            >
              Enregistrer
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              sx={{ textTransform: 'none' }}
              onClick={() => setEditing(true)}
            >
              Modifier
            </Button>
            <Button
              size="small"
              color="error"
              sx={{ textTransform: 'none' }}
              onClick={() => {
                if (
                  window.confirm(
                    `Supprimer le badge de ${name} ? Il n'ouvrira plus les portes et libère une place.`
                  )
                )
                  ticketingRepository
                    .cancelExhibitorBadge(code, badge.id)
                    .then(onChanged)
                    .catch((e: Error) => onError(e.message));
              }}
            >
              Supprimer
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

/**
 * Page of an exhibitor, opened with its code (no account): it registers its
 * staff in one go and downloads every badge in one PDF.
 */
export default function ExhibitorBadges(): JSX.Element {
  const { code = '' } = useParams();
  const { ticketingRepository } = useApiContext();
  const [page, setPage] = useState<iExhibitorPassPage | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    ticketingRepository
      .getExhibitorPassPage(code)
      .then(setPage)
      .catch((e: Error) => setLoadError(e.message));
  }, [code]);

  if (loadError)
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', p: 4 }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  if (!page)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress size={32} sx={{ color: '#111' }} />
      </Box>
    );

  const accent = page.event.accent;
  const filled = rows.filter(
    (r) => r.firstname.trim() !== '' || r.lastname.trim() !== ''
  );
  const incomplete = filled.some(
    (r) => r.firstname.trim() === '' || r.lastname.trim() === ''
  );
  const tooMany = filled.length > page.left;
  const setRow = (i: number, patch: Partial<Row>): void =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const button = {
    textTransform: 'none',
    bgcolor: accent,
    boxShadow: 'none',
    borderRadius: 0,
    fontWeight: 600,
    '&:hover': { bgcolor: accent, filter: 'brightness(.9)', boxShadow: 'none' },
  } as const;

  const submit = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await ticketingRepository.addExhibitorBadges(
        code,
        filled.map((x) => ({
          firstname: x.firstname.trim(),
          lastname: x.lastname.trim(),
          job_title: x.job_title.trim(),
        })),
        filled.map((x) => x.photo)
      );
      setPage(r);
      setRows([emptyRow()]);
      setMessage({
        type: 'success',
        text: `${
          r.created ?? filled.length
        } badge(s) créé(s). Vous les recevez aussi par e-mail (${
          r.exhibitor.contact_email
        }).`,
      });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9fafb' }}>
      <Box
        sx={{
          bgcolor: accent,
          color: '#fff',
          ...(page.event.banner_url
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,.38), rgba(0,0,0,.38)), url(${page.event.banner_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {}),
        }}
      >
        <Box
          sx={{
            maxWidth: 860,
            mx: 'auto',
            px: 2,
            py: 5,
            display: 'flex',
            gap: 3,
            alignItems: 'center',
          }}
        >
          {page.event.logo_url && (
            <img
              src={page.event.logo_url}
              alt=""
              style={{
                height: 64,
                maxWidth: 160,
                objectFit: 'contain',
                ...(page.event.logo_transparent
                  ? {}
                  : { background: '#fff', padding: 6 }),
              }}
            />
          )}
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
              }}
            >
              Badges exposant
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
              {page.event.name}
            </Typography>
            {page.event.subtitle && (
              <Typography sx={{ fontSize: 17 }}>
                {page.event.subtitle}
              </Typography>
            )}
            <Typography sx={{ opacity: 0.9 }}>
              {page.event.dates}
              {page.event.city ? ` · ${page.event.city}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 860, mx: 'auto', px: 2, py: 4 }}>
        {message && (
          <Alert
            severity={message.type}
            onClose={() => setMessage(null)}
            sx={{ mb: 2, borderRadius: 0 }}
          >
            {message.text}
          </Alert>
        )}

        <Box
          sx={{
            ...box,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              {page.exhibitor.company}
            </Typography>
            <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
              <b style={{ color: '#111' }}>{page.used}</b> badge(s) créé(s) sur{' '}
              {page.quota} · {page.left} restant(s) · code{' '}
              <span style={{ fontFamily: 'monospace' }}>{page.code}</span>
            </Typography>
          </Box>
          <Button
            variant="contained"
            href={page.pdf_url}
            disabled={page.used === 0}
            sx={button}
          >
            Télécharger tous les badges (PDF)
          </Button>
        </Box>

        {page.left > 0 ? (
          <Box sx={box}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
                Ajouter des personnes
              </Typography>
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => setPasting(!pasting)}
              >
                {pasting ? 'Saisir à la main' : 'Coller une liste (Excel)'}
              </Button>
            </Box>
            <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 2 }}>
              Prénom et nom obligatoires. La photo, facultative, est imprimée
              sur le badge (format portrait, visage bien visible).
            </Typography>

            {pasting && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  multiline
                  minRows={5}
                  fullWidth
                  placeholder={
                    'Julie\tMartin\tŒnologue\nHugo\tMartin\tCommercial'
                  }
                  helperText="Copiez les colonnes Prénom, Nom, Fonction depuis votre tableur et collez-les ici (une personne par ligne)."
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                />
                <Button
                  size="small"
                  variant="contained"
                  sx={{ ...button, mt: 1 }}
                  disabled={pasted.trim() === ''}
                  onClick={() => {
                    const parsed = parsePasted(pasted);
                    const kept = rows.filter(
                      (r) =>
                        r.firstname.trim() !== '' || r.lastname.trim() !== ''
                    );
                    setRows([...kept, ...parsed].slice(0, 100));
                    setPasted('');
                    setPasting(false);
                  }}
                >
                  Ajouter ces personnes à la liste
                </Button>
              </Box>
            )}

            {rows.map((r, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr 1fr',
                    md: '1fr 1fr 1fr 170px 64px',
                  },
                  gap: 1,
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <TextField
                  size="small"
                  label="Prénom"
                  value={r.firstname}
                  onChange={(e) => setRow(i, { firstname: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Nom"
                  value={r.lastname}
                  onChange={(e) => setRow(i, { lastname: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Fonction"
                  value={r.job_title}
                  onChange={(e) => setRow(i, { job_title: e.target.value })}
                />
                <PhotoPicker
                  file={r.photo}
                  onChange={(f) => setRow(i, { photo: f })}
                />
                <Button
                  size="small"
                  sx={{ textTransform: 'none', color: '#666', minWidth: 0 }}
                  aria-label="Retirer la ligne"
                  disabled={rows.length === 1}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                >
                  Retirer
                </Button>
              </Box>
            ))}

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mt: 2,
                flexWrap: 'wrap',
              }}
            >
              <Button
                size="small"
                sx={{ textTransform: 'none' }}
                disabled={rows.length >= page.left}
                onClick={() => setRows([...rows, emptyRow()])}
              >
                + Ajouter une personne
              </Button>
              <Box sx={{ flex: 1 }} />
              {tooMany && (
                <Typography sx={{ fontSize: 13, color: '#b91c1c' }}>
                  {filled.length} personnes pour {page.left} badge(s) restant(s)
                </Typography>
              )}
              <Button
                variant="contained"
                sx={button}
                disabled={
                  saving || filled.length === 0 || incomplete || tooMany
                }
                onClick={submit}
              >
                {saving
                  ? 'Création…'
                  : `Créer ${filled.length || ''} badge(s)`.replace('  ', ' ')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 0 }}>
            Tous vos badges sont utilisés. Pour en obtenir d&apos;autres,
            contactez l&apos;organisateur
            {page.event.contact_email ? ` (${page.event.contact_email})` : ''}.
            Vous pouvez supprimer un badge pour libérer une place.
          </Alert>
        )}

        {page.badges.length > 0 && (
          <Box sx={box}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>
              Votre équipe ({page.badges.length})
            </Typography>
            {page.badges.map((b) => (
              <BadgeRow
                key={`${b.id}-${b.photo_url ?? ''}-${b.firstname}-${
                  b.lastname
                }-${b.job_title ?? ''}`}
                badge={b}
                code={code}
                accent={accent}
                onChanged={setPage}
                onError={(m) => setMessage({ type: 'error', text: m })}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
